import type {
  ContentI18n,
  NpsQuestion,
  NpsSurvey,
  QuestionMetadata,
  SurveyLang,
  SurveyTranslatableLang,
} from '@/shared/types/nps';

export const SURVEY_TRANSLATABLE_LANGS: SurveyTranslatableLang[] = ['pt-PT', 'en', 'es'];

export const SURVEY_LANG_LABEL: Record<SurveyLang, string> = {
  'pt-BR': 'PT-BR',
  'pt-PT': 'PT-PT',
  en: 'English',
  es: 'Español',
};

function isTranslatable(lang: string): lang is SurveyTranslatableLang {
  return (SURVEY_TRANSLATABLE_LANGS as string[]).includes(lang);
}

function localizeOptions(metadata: QuestionMetadata | undefined, lang: SurveyTranslatableLang): QuestionMetadata | undefined {
  if (!metadata?.options?.length) return metadata;
  return {
    ...metadata,
    options: metadata.options.map((opt) => {
      const localized = opt.labels?.[lang];
      if (!localized) return opt;
      return { ...opt, label: localized };
    }),
  };
}

export function localizeQuestion(question: NpsQuestion, lang: SurveyLang): NpsQuestion {
  if (lang === 'pt-BR' || !isTranslatable(lang)) return question;
  const entry = question.i18n?.[lang];
  return {
    ...question,
    title: entry?.title?.trim() || question.title,
    description:
      entry?.description !== undefined && entry.description !== null
        ? entry.description
        : question.description,
    response_metadata: localizeOptions(question.response_metadata, lang) ?? question.response_metadata,
  };
}

/** Projeta survey + perguntas para o idioma (shape flat, como a api-v2). */
export function localizeSurvey(survey: NpsSurvey, lang: SurveyLang): NpsSurvey {
  if (lang === 'pt-BR' || !isTranslatable(lang)) return survey;
  const entry = survey.i18n?.[lang];
  return {
    ...survey,
    title: entry?.title?.trim() || survey.title,
    description:
      entry?.description !== undefined && entry.description !== null
        ? entry.description
        : survey.description,
    questions: survey.questions?.map((q) => localizeQuestion(q, lang)),
  };
}

export function emptyContentI18n(): ContentI18n {
  return {};
}

/** Remove entradas vazias do mapa i18n. */
export function compactContentI18n(i18n: ContentI18n | null | undefined): ContentI18n | undefined {
  if (!i18n) return undefined;
  const out: ContentI18n = {};
  for (const lang of SURVEY_TRANSLATABLE_LANGS) {
    const entry = i18n[lang];
    if (!entry) continue;
    const title = entry.title?.trim();
    const description = entry.description?.trim();
    if (!title && !description) continue;
    out[lang] = {
      ...(title ? { title } : {}),
      ...(description ? { description } : {}),
    };
  }
  return Object.keys(out).length ? out : undefined;
}

/** Updater funcional evita merge com snapshot stale do `watch`. */
export type ContentI18nUpdater =
  | ContentI18n
  | ((prev: ContentI18n | null | undefined) => ContentI18n);

export function applyContentI18nUpdate(
  prev: ContentI18n | null | undefined,
  update: ContentI18nUpdater,
): ContentI18n {
  return typeof update === 'function' ? update(prev) : update;
}
